import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { LuBookOpenCheck, LuUsers, LuClock3, LuPackageCheck, LuX, LuCheck } from "react-icons/lu";
import axios from "../../../axios";
import TopPageTitle from "../../../components/TopPageTitle/TopPageTitle";
import Loader from "../../../components/Loader/Loader";
import StarIcon from "../../../components/ui/StarIcon";
import { PagesWrapper } from "../../../styles/components/PagesWrapper.styled";
import { setStars } from "../../../redux/slices/auth";
import GiftForm from "./GiftForm";
import ShopModal from "./ShopModal";
import { KIND_STYLES } from "./kindStyles";
import * as styled from "./Shop.styled";

const STATUS = {
  new: { label: "Обробляється", Icon: LuClock3 },
  done: { label: "Видано", Icon: LuPackageCheck },
  cancelled: { label: "Скасовано, зірки повернуто", Icon: LuX },
};

const EARN_WAYS = [
  { Icon: LuBookOpenCheck, text: "Здане домашнє завдання", amount: 1 },
  { Icon: LuUsers, text: "Друг, якому ти подарував урок, купив пакет", amount: 20 },
];

function Shop() {
  const dispatch = useDispatch();
  const [items, setItems] = useState([]);
  const [balance, setBalance] = useState(0);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);
  const [confirmItem, setConfirmItem] = useState(null);
  const [giftItem, setGiftItem] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    try {
      const [shop, myOrders] = await Promise.all([axios.get("shop/items"), axios.get("shop/orders/me")]);
      setItems(shop.data.items);
      setBalance(shop.data.balance);
      dispatch(setStars(shop.data.balance));
      setOrders(myOrders.data);
    } catch (error) {
      setNotice({ type: "error", text: "Не вдалося завантажити магазин. Спробуй оновити сторінку." });
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  // Найближча нагорода, на яку ще не вистачає — для прогресу в гаманці
  const nextGoal = useMemo(
    () =>
      items
        .filter((item) => !item.limitReached && item.priceStars > balance)
        .sort((a, b) => a.priceStars - b.priceStars)[0],
    [items, balance]
  );

  const buy = async (item, friend = {}) => {
    setBuying(true);
    setNotice(null);
    try {
      const { data } = await axios.post("shop/orders", { itemId: item._id, ...friend });
      dispatch(setStars(data.balance));
      setGiftItem(null);
      setConfirmItem(null);
      setNotice({
        type: "success",
        text:
          item.kind === "gift_friend"
            ? "Подарунок оформлено! Ми зв'яжемося з твоїм другом найближчим часом."
            : "Готово! Менеджер зв'яжеться з тобою найближчим часом.",
      });
      await load();
    } catch (error) {
      const text = error.response?.data?.message || "Не вдалося оформити замовлення. Спробуй ще раз.";
      if (item.kind === "gift_friend") throw new Error(text);
      setConfirmItem(null);
      setNotice({ type: "error", text });
    } finally {
      setBuying(false);
    }
  };

  const confirmKind = confirmItem ? KIND_STYLES[confirmItem.kind] || KIND_STYLES.other : null;

  const handleBuyClick = (item) => (item.kind === "gift_friend" ? setGiftItem(item) : setConfirmItem(item));

  return (
    <PagesWrapper>
      <TopPageTitle value={"Магазин"} />
      {loading ? (
        <Loader />
      ) : (
        <styled.Page>
          <styled.Wallet>
            <styled.WalletMain>
              <styled.WalletLabel>Твій баланс</styled.WalletLabel>
              <styled.WalletBalance>
                {balance}
                <StarIcon size={44} />
              </styled.WalletBalance>
              {nextGoal ? (
                <styled.Goal>
                  <styled.GoalText>
                    До наступної нагороди — ще <b>{nextGoal.priceStars - balance}</b> <StarIcon size={14} />
                  </styled.GoalText>
                  <styled.Progress $dark>
                    <styled.ProgressFill style={{ width: `${Math.min(100, (balance / nextGoal.priceStars) * 100)}%` }} />
                  </styled.Progress>
                </styled.Goal>
              ) : (
                items.length > 0 && (
                  <styled.GoalText>
                    Вистачає на будь-яку нагороду <StarIcon size={14} />
                  </styled.GoalText>
                )
              )}
            </styled.WalletMain>

            <styled.Earn>
              <styled.EarnTitle>Як заробити</styled.EarnTitle>
              {EARN_WAYS.map(({ Icon, text, amount }) => (
                <styled.EarnRow key={text}>
                  <styled.EarnIcon>
                    <Icon size={18} />
                  </styled.EarnIcon>
                  <styled.EarnText>{text}</styled.EarnText>
                  <styled.EarnAmount>
                    +{amount} <StarIcon size={16} />
                  </styled.EarnAmount>
                </styled.EarnRow>
              ))}
            </styled.Earn>
          </styled.Wallet>

          {notice && (
            <styled.Notice $type={notice.type} onClick={() => setNotice(null)}>
              {notice.type === "success" ? <LuCheck size={18} /> : <LuX size={18} />}
              {notice.text}
            </styled.Notice>
          )}

          <styled.SectionTitle>Обміняй зірки</styled.SectionTitle>
          <styled.Grid>
            {items.map((item) => {
              const kind = KIND_STYLES[item.kind] || KIND_STYLES.other;
              const progress = Math.min(100, (balance / item.priceStars) * 100);
              return (
                <styled.Card key={item._id}>
                  <styled.CardArt $tint={kind.tint} $color={kind.color}>
                    <kind.Icon size={52} strokeWidth={1.6} />
                    <styled.PricePill>
                      {item.priceStars} <StarIcon size={16} />
                    </styled.PricePill>
                  </styled.CardArt>
                  <styled.CardBody>
                    <styled.CardTitle>{item.title}</styled.CardTitle>
                    {item.description && <styled.CardText>{item.description}</styled.CardText>}
                  </styled.CardBody>
                  <styled.CardFooter>
                    {item.limitReached ? (
                      <styled.Done>
                        <LuCheck size={18} /> Вже отримано
                      </styled.Done>
                    ) : item.canBuy ? (
                      <styled.PrimaryButton disabled={buying} onClick={() => handleBuyClick(item)}>
                        {item.kind === "gift_friend" ? "Подарувати" : "Обміняти"}
                      </styled.PrimaryButton>
                    ) : (
                      <styled.Locked>
                        <styled.Progress>
                          <styled.ProgressFill style={{ width: `${progress}%` }} />
                        </styled.Progress>
                        <styled.LockedText>
                          {balance} з {item.priceStars} <StarIcon size={14} /> · ще {item.priceStars - balance}
                        </styled.LockedText>
                      </styled.Locked>
                    )}
                  </styled.CardFooter>
                </styled.Card>
              );
            })}
          </styled.Grid>

          {orders.length > 0 && (
            <>
              <styled.SectionTitle>Мої замовлення</styled.SectionTitle>
              <styled.Orders>
                {orders.map((order) => {
                  const kind = KIND_STYLES[order.kind] || KIND_STYLES.other;
                  const status = STATUS[order.status];
                  return (
                    <styled.Order key={order._id}>
                      <styled.OrderIcon $tint={kind.tint} $color={kind.color}>
                        <kind.Icon size={20} />
                      </styled.OrderIcon>
                      <styled.OrderMain>
                        <styled.OrderTitle>{order.title}</styled.OrderTitle>
                        <styled.OrderMeta>
                          {new Date(order.createdAt).toLocaleDateString("uk-UA")} · {order.priceStars}{" "}
                          <StarIcon size={12} />
                          {order.friendName && ` · для ${order.friendName}`}
                        </styled.OrderMeta>
                      </styled.OrderMain>
                      <styled.Status $status={order.status}>
                        <status.Icon size={14} />
                        {status.label}
                      </styled.Status>
                    </styled.Order>
                  );
                })}
              </styled.Orders>
            </>
          )}
        </styled.Page>
      )}

      {confirmItem && (
        <ShopModal onClose={() => setConfirmItem(null)}>
          <styled.ModalIcon $tint={confirmKind.tint} $color={confirmKind.color}>
            <confirmKind.Icon size={32} />
          </styled.ModalIcon>
          <styled.ModalTitle>{confirmItem.title}</styled.ModalTitle>
          <styled.ModalText>
            Спишемо {confirmItem.priceStars} <StarIcon size={16} />, у тебе залишиться {balance - confirmItem.priceStars}{" "}
            <StarIcon size={16} />. Менеджер зв'яжеться з тобою, щоб усе оформити.
          </styled.ModalText>
          <styled.PrimaryButton disabled={buying} onClick={() => buy(confirmItem)}>
            {buying ? (
              "Оформлюємо..."
            ) : (
              <>
                Обміняти за {confirmItem.priceStars} <StarIcon size={18} />
              </>
            )}
          </styled.PrimaryButton>
          <styled.TextButton onClick={() => setConfirmItem(null)}>Скасувати</styled.TextButton>
        </ShopModal>
      )}

      {giftItem && (
        <GiftForm item={giftItem} onSubmit={(friend) => buy(giftItem, friend)} onClose={() => setGiftItem(null)} />
      )}
    </PagesWrapper>
  );
}

export default Shop;
